/** @jsx jsx */
import { SerializedStyles, css, jsx } from "@emotion/core"

import React, { useRef } from "react"

import { Icon } from "@cd2/components"
import { useTheme } from "@cd2/theme/build/es6"
import { IComponentProp } from "@cd2/components/build/es6/types/utils"
import classnames from "classnames"
import { formPadding } from "../utils/styleUtils"
import { Popover, PopoverRef } from "./Popover"

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace PopoutInput {
  export interface IContext {
    popover: PopoverRef
  }
}

interface ITheme {
  main: {
    padding: number | string
  }
}

export function PopoutInput({
  renderMain,
  renderOptions,
  label,
  error,
  placeholder,
  showRequiredStar,
  onHidden,
  onShown,
  rightIcon,
  placement,
  disabled,
  extendTheme,
  noChrome,
  maxWidth,
  optionsContainerCss,
  inline,
}: {
  renderMain(): React.ReactNode
  renderOptions(ctx: PopoutInput.IContext): React.ReactNode
  label?: React.ReactNode
  error?: string | boolean
  placeholder?: string
  showRequiredStar?: boolean
  disabled?: boolean
  onHidden?(): void
  onShown?(): void
  rightIcon?: string
  placement?: IComponentProp<typeof Popover, "placement">
  extendTheme?: ITheme
  noChrome?: boolean
  maxWidth?: number | null
  optionsContainerCss?: SerializedStyles
  inline?: boolean
}) {
  const instanceRef = useRef<PopoverRef>(null)
  const theme = useTheme()

  function handleOpen() {
    if (!instanceRef.current) {
      return
    }

    instanceRef.current.show()
  }

  const labelNode = label && (
    <label
      css={css`
        display: block;
        font-weight: ${theme.forms.label?.font?.weight};
        font-size: ${theme.forms.label?.font?.size}px;
        color: ${error ? theme.themeColor("error") : theme.forms.label?.font?.color};
        margin-bottom: ${theme.forms.label?.spacing}px;
      `}
    >
      {label}{" "}
      {showRequiredStar && (
        <span
          css={css`
            color: red;
          `}
        >
          *
        </span>
      )}
    </label>
  )

  if (inline) {
    return (
      <div
        css={
          disabled
            ? css`
                opacity: 0.5;
                cursor: not-allowed;
              `
            : undefined
        }
      >
        {labelNode}
        <div
          tabIndex={0}
          css={css`
            font-size: ${theme.forms.font.size};

            ${!noChrome &&
            css`
              border-radius: ${theme.forms.border.radius ?? theme.borderRadius}px;
              border: 1px solid ${error ? theme.themeColor("error") : theme.forms.border.color};
              padding: ${extendTheme?.main?.padding ??
              formPadding(theme.forms.padding ?? { topAndBottom: 10, sides: 9 })};
              padding-right: 9px;
              display: grid;
              grid-template-columns: auto max-content;
              align-items: center;
              gap: 8px;
              background-color: ${theme.themeColor("white")};
            `}
          `}
        >
          <span
            css={css`
              text-overflow: ellipsis;
              overflow: hidden;
            `}
          >
            {renderMain() ??
              (placeholder ? (
                <span
                  css={css`
                    color: ${theme.forms.placeholderColor};
                  `}
                >
                  {placeholder}
                </span>
              ) : (
                <i>-</i>
              ))}
          </span>
        </div>
        {error && typeof error === "string" && (
          <div
            css={css`
              color: ${theme.themeColor("error")};
              font-size: 0.9em;
              margin: 2px 0;
            `}
          >
            {error}
          </div>
        )}
        <div
          css={css(
            css`
              border: 1px solid ${theme.forms.border.color};
              border-radius: ${theme.forms.border.radius ?? theme.borderRadius}px;
              box-shadow: ${theme.boxShadow.sm};
              overflow: hidden;
              background: white;
              min-width: 200px !important;
            `,
            optionsContainerCss,
          )}
        >
          {renderOptions({
            get popover() {
              return instanceRef.current!
            },
          })}
        </div>
      </div>
    )
  }

  return (
    <div
      css={
        disabled
          ? css`
              opacity: 0.5;
              cursor: not-allowed;
            `
          : undefined
      }
    >
      <div
        css={
          disabled
            ? css`
                pointer-events: none;
              `
            : undefined
        }
      >
        <Popover
          ref={instanceRef}
          onHidden={onHidden}
          onShow={onShown}
          placement={placement ?? "bottom-left"}
          offset={[0, 4]}
          maxWidth={maxWidth !== undefined ? maxWidth : 400}
          getContent={() => (
            <div
              css={css(
                css`
                  border: 1px solid ${theme.forms.border.color};
                  border-radius: ${theme.forms.border.radius ?? theme.borderRadius}px;
                  box-shadow: ${theme.boxShadow.sm};
                  overflow: hidden;
                  background: white;
                  min-width: 200px !important;
                `,
                optionsContainerCss,
              )}
            >
              {renderOptions({
                get popover() {
                  return instanceRef.current!
                },
              })}
            </div>
          )}
          trigger="click"
          interactive
        >
          <div
            css={css`
              cursor: pointer;
            `}
          >
            {labelNode}
            <div
              tabIndex={0}
              css={css`
                font-size: ${theme.forms.font.size};

                ${!noChrome &&
                css`
                  border-radius: ${theme.forms.border.radius ?? theme.borderRadius}px;
                  border: 1px solid ${error ? theme.themeColor("error") : theme.forms.border.color};
                  padding: ${extendTheme?.main?.padding ??
                  formPadding(theme.forms.padding ?? { topAndBottom: 10, sides: 9 })};
                  padding-right: 9px;
                  display: grid;
                  grid-template-columns: auto max-content;
                  align-items: center;
                  gap: 8px;
                  background-color: ${theme.themeColor("white")};
                `}
              `}
              onKeyPress={e => {
                if (e.key === "Enter" || e.key === " ") {
                  handleOpen()
                }
              }}
            >
              <span
                css={css`
                  text-overflow: ellipsis;
                  overflow: hidden;
                `}
              >
                {renderMain() ??
                  (placeholder ? (
                    <span
                      css={css`
                        color: ${theme.forms.placeholderColor};
                      `}
                    >
                      {placeholder}
                    </span>
                  ) : (
                    <i>-</i>
                  ))}
              </span>
              {!noChrome && <Icon icon={rightIcon ?? "chevron-down"} />}
            </div>
            {error && typeof error === "string" && (
              <div
                css={css`
                  color: ${theme.themeColor("error")};
                  font-size: 0.9em;
                  margin: 2px 0;
                `}
              >
                {error}
              </div>
            )}
          </div>
        </Popover>
      </div>
    </div>
  )
}

PopoutInput.OptionsList = function ({
  options,
  onOptionClick,
  className,
}: {
  options: (
    | false
    | "separator"
    | { render(): React.ReactNode; key?: string | number }
    | {
        key?: string | number
        label: React.ReactNode
        onClick(): void
        disabled?: boolean
        className?: string
      }
  )[]
  onOptionClick?(): void
  className?: string
}) {
  return (
    <div
      className={className}
      css={css`
        > .option {
          padding: 8px;
          transition: 0.1s;
          cursor: pointer;

          &.disabled {
            opacity: 0.5;
            cursor: default;
          }

          :hover {
            background: #efefef;
          }
        }

        > .separator {
          border-bottom: 1px solid #dfdfdf;
        }
      `}
    >
      {options.map((option, i) => {
        if (option === false) {
          return null
        } else if (option === "separator") {
          return <div key={`separator-${i}`} className="separator" />
        } else if ("render" in option) {
          return (
            <React.Fragment key={option.key ?? `render-${i}`}>{option.render()}</React.Fragment>
          )
        } else {
          return (
            <div
              key={option.key ?? `option-${i}`}
              className={classnames("option", option.className, option.disabled && "disabled")}
              onClick={() => {
                if (option.disabled) {
                  return
                }
                onOptionClick?.()
                option.onClick()
              }}
            >
              {option.label}
            </div>
          )
        }
      })}
    </div>
  )
}
